import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import AppModal from './AppModal';

// Replaces native confirm(). The native one blocks the renderer until a human
// answers, which halts every automated run. This keeps the call site reading the
// same way — `if (await confirm({ ... }))` — so swapping a confirm() for this is a
// swap, not a rewrite.

export interface ConfirmOptions {
  message: string;
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Styles the confirm action as destructive (deletes, irreversible changes). */
  destructive?: boolean;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function useConfirm(): ConfirmFn {
  const confirm = useContext(ConfirmContext);
  // Fail fast. Silently resolving would mean either destroying data without
  // asking, or never destroying it — both worse than a crash at wire-up time.
  if (!confirm) throw new Error('useConfirm must be used within a ConfirmProvider');
  return confirm;
}

interface PendingConfirm {
  options: ConfirmOptions;
  resolve: (answer: boolean) => void;
}

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const pendingRef = useRef<PendingConfirm | null>(null);
  pendingRef.current = pending;

  const confirm = useCallback<ConfirmFn>(
    (options) => new Promise<boolean>((resolve) => setPending({ options, resolve })),
    [],
  );

  const answer = useCallback((value: boolean) => {
    const current = pendingRef.current;
    setPending(null);
    current?.resolve(value);
  }, []);

  // Escape resolves false. An unanswered promise would leave the caller awaiting
  // forever, so dismissal must still settle it — and it must settle as "no", so a
  // stray keypress can never destroy data.
  useEffect(() => {
    if (!pending) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') answer(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [pending, answer]);

  const options = pending?.options;
  const titleId = 'app-confirm-title';

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {options && (
        <AppModal
          isOpen
          onClose={() => answer(false)}
          width={440}
          showClose={false}
          persistent
          header={options.title ? <span id={titleId}>{options.title}</span> : undefined}
        >
          <div role="dialog" aria-modal="true" aria-labelledby={options.title ? titleId : undefined} aria-label={options.title ? undefined : options.message}>
            <p style={MESSAGE_STYLE}>{options.message}</p>
            <div style={ACTIONS_STYLE}>
              <button type="button" onClick={() => answer(false)} style={CANCEL_STYLE}>
                {options.cancelLabel ?? 'Cancel'}
              </button>
              <button
                type="button"
                onClick={() => answer(true)}
                style={{ ...CONFIRM_STYLE, ...(options.destructive ? DESTRUCTIVE_STYLE : {}) }}
                autoFocus
              >
                {options.confirmLabel ?? 'OK'}
              </button>
            </div>
          </div>
        </AppModal>
      )}
    </ConfirmContext.Provider>
  );
}

const MESSAGE_STYLE: React.CSSProperties = { margin: '4px 0 20px', fontSize: 14, lineHeight: 1.5 };

const ACTIONS_STYLE: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
};

const BUTTON_BASE: React.CSSProperties = {
  padding: '7px 16px',
  borderRadius: 4,
  fontSize: 14,
  cursor: 'pointer',
  borderWidth: 1,
  borderStyle: 'solid',
};

const CANCEL_STYLE: React.CSSProperties = {
  ...BUTTON_BASE,
  background: '#f1f1f1',
  borderColor: '#c9c9c9',
  color: '#222222',
};

const CONFIRM_STYLE: React.CSSProperties = {
  ...BUTTON_BASE,
  background: '#1f4e8c',
  borderColor: '#173865',
  color: '#ffffff',
};

// Destructive actions separate from the safe action by LUMINANCE, not by red-vs-green.
const DESTRUCTIVE_STYLE: React.CSSProperties = {
  background: '#7a1414',
  borderColor: '#5a0f0f',
};
